import React, { FC, ReactElement, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import MenuItem from '@mui/material/MenuItem';
import { Theme } from '@mui/material/styles';
// Components
import Text from '../text';
import InputField from '../form/input';
import ModalButton, { ButtonType } from './templates/modalButton';
import ModalButtonContainer from './templates/modalButtonContainer';
// Modules
import { CoverageVoucher, VoucherValueType } from '../../core/apiTypes';
// Resources
import { stringsRes } from '../../resources/strings';
import { PropsFromModal } from './createModalHOC';

export interface VoucherFormData {
    name: string;
    amount: string;
    valueType: VoucherValueType;
}

interface VoucherFormProps extends PropsFromModal {
    mode?: 'add' | 'edit';
    voucher?: CoverageVoucher;
    description?: string;
    /** Returns a field name -> error message map. Any entry blocks the confirm button. */
    validate?: (data: VoucherFormData) => Record<string, string>;
    onConfirmAction?: (data: VoucherFormData) => void;
    onCancelAction?: () => void;
    onDeleteAction?: () => void;
}

const MAX_PERCENTAGE = 100;

const VoucherForm: FC<VoucherFormProps> = (props): ReactElement => {
    const {
        onRequestClose,
        mode = 'add',
        voucher,
        description,
        validate,
        onConfirmAction,
        onCancelAction,
        onDeleteAction,
    } = props;
    const classes = useStyles();
    const isEdit = mode === 'edit';

    const [data, setData] = useState<VoucherFormData>({
        name: voucher?.name ?? '',
        amount: voucher ? `${voucher.value}` : '',
        valueType: voucher?.value_type ?? 'fixed',
    });

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setData({ ...data, [e.target.name]: e.target.value });
    };

    const amountError = (): string => {
        if (!data.amount) {
            return '';
        }
        const amount = Number(data.amount.replace(',', '.'));
        if (!amount || amount <= 0) {
            return stringsRes.admin.payment.voucher.form.invalidAmount;
        }
        if (data.valueType === 'percentage' && amount > MAX_PERCENTAGE) {
            return stringsRes.admin.payment.voucher.form.invalidPercentage;
        }
        return '';
    };

    const errors: Record<string, string> = { ...validate?.(data) };
    const currentAmountError = amountError();
    if (currentAmountError) {
        errors.amount = currentAmountError;
    }
    const hasError = Object.keys(errors).length > 0;
    const isIncomplete = !data.name?.trim() || !data.amount;

    const onClickConfirm = (): void => {
        if (hasError || isIncomplete) {
            return;
        }
        onConfirmAction?.(data);
        onRequestClose();
    };

    const onClickCancel = (): void => {
        onCancelAction?.();
        onRequestClose();
    };

    const onClickDelete = (): void => {
        onDeleteAction?.();
        onRequestClose();
    };

    return (
        <div className="LCVoucherFormContainer">
            {description && <Text.Description16 customColor="grey" text={description} />}
            <div className={classes.formContent}>
                <InputField
                    autoFocus
                    required
                    name="name"
                    value={data.name}
                    onChange={onChange}
                    label={stringsRes.admin.payment.voucher.form.name}
                    className={classes.nameInput}
                    errorText={errors.name}
                />
                <InputField
                    required
                    onlyNumber
                    allowDigit
                    name="amount"
                    value={data.amount}
                    onChange={onChange}
                    label={stringsRes.admin.payment.voucher.form.amount}
                    className={classes.amountInput}
                    errorText={errors.amount}
                />
                <InputField
                    select
                    name="valueType"
                    value={data.valueType}
                    onChange={onChange}
                    label={stringsRes.admin.payment.voucher.form.format}
                    className={classes.formatInput}
                    // The default end adornment of InputField would collide with the select chevron
                    InputProps={{ disableUnderline: true }}
                >
                    <MenuItem value="fixed">{stringsRes.admin.payment.voucher.form.amountUnit}</MenuItem>
                    <MenuItem value="percentage">{stringsRes.admin.payment.voucher.form.percentageUnit}</MenuItem>
                </InputField>
            </div>
            <ModalButtonContainer>
                {isEdit && onDeleteAction && (
                    <ModalButton type={ButtonType.ERROR} onClick={onClickDelete} label={stringsRes.common.delete} />
                )}
                <div className={classes.flex} />
                <ModalButton type={ButtonType.CANCEL} onClick={onClickCancel} label={stringsRes.common.cancel} />
                <ModalButton
                    type={ButtonType.CONFIRM}
                    onClick={onClickConfirm}
                    disabled={hasError || isIncomplete}
                    label={isEdit ? stringsRes.common.edit : stringsRes.common.add}
                />
            </ModalButtonContainer>
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formContent: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: theme.spacing(1.5),
            marginTop: theme.spacing(2),
            '@media (max-width: 600px)': {
                flexDirection: 'column',
            },
        },
        nameInput: {
            flex: 1,
            minWidth: 0,
            '@media (max-width: 600px)': {
                width: '100%',
            },
        },
        amountInput: {
            width: 120,
            '@media (max-width: 600px)': {
                width: '100%',
            },
        },
        formatInput: {
            width: 120,
            '@media (max-width: 600px)': {
                width: '100%',
            },
        },
        flex: {
            display: 'flex',
            flex: 1,
        },
    }),
);

export default VoucherForm;
